import type { ReactNode } from "react";
import { AlertTriangle, Inbox, LoaderCircle } from "lucide-react";

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-slate-950">{title}</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">{description}</p>
      </div>
      {actions}
    </div>
  );
}

export function LoadingState({ label = "Loading scheduling data..." }: { label?: string }) {
  return (
    <div className="panel flex min-h-52 items-center justify-center gap-3 p-8 text-sm font-medium text-slate-500">
      <LoaderCircle className="h-5 w-5 animate-spin text-brand-600" />
      {label}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="panel flex min-h-40 items-center justify-center gap-3 border-rose-200 bg-rose-50 p-8 text-sm font-medium text-rose-700">
      <AlertTriangle className="h-5 w-5" />
      {error instanceof Error ? error.message : "Unable to load this view."}
    </div>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="panel flex min-h-48 flex-col items-center justify-center p-8 text-center">
      <Inbox className="mb-3 h-8 w-8 text-slate-300" />
      <h2 className="font-bold text-slate-800">{title}</h2>
      <p className="mt-1 max-w-lg text-sm text-slate-500">{description}</p>
    </div>
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    critical: "bg-rose-100 text-rose-800",
    high: "bg-amber-100 text-amber-800",
    medium: "bg-sky-100 text-sky-800",
    low: "bg-slate-100 text-slate-700",
  };
  return <span className={`badge ${colors[priority] ?? colors.low}`}>{priority}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-800",
    complete: "bg-emerald-100 text-emerald-800",
    published: "bg-indigo-100 text-indigo-800",
    open: "bg-rose-100 text-rose-800",
    acknowledged: "bg-amber-100 text-amber-800",
    resolved: "bg-emerald-100 text-emerald-800",
    draft: "bg-slate-100 text-slate-700",
    processing: "bg-sky-100 text-sky-800",
    uploading: "bg-sky-100 text-sky-800",
    superseded: "bg-slate-100 text-slate-600",
    failed: "bg-rose-100 text-rose-800",
    added: "bg-emerald-100 text-emerald-800",
    removed: "bg-rose-100 text-rose-800",
    changed: "bg-amber-100 text-amber-800",
    unchanged: "bg-slate-100 text-slate-600",
  };
  return <span className={`badge ${colors[status] ?? colors.draft}`}>{status}</span>;
}
